// 📄 components/Configuracion/ConfiguracionApis.tsx
// Un cliente puede consumir varias APIs de distintos sistemas (Ariba,
// S/4HANA Cloud, ...), cada una con sus propias credenciales (cuya forma
// depende del sistema — ver apiTiposConfig.ts), su propia URL Base + API
// y su propia lista de endpoints — por eso esto es una lista agrupada por
// tipo de ApiDefinitionCard en vez de un único formulario. Los campos
// secretos de cada API nunca vuelven en el GET (solo si están guardados o
// no, por API); un campo en blanco al guardar NO borra el secreto ya
// guardado para esa misma API (ver guardarClienteApis en configService.js).
//
// Alta/edición/eliminación persisten DIRECTO contra Firestore desde el
// modal (NuevaApiModal.tsx) o desde acá mismo (eliminar) — ya no hay un
// botón "Guardar configuración" al final de la página que agrupe cambios
// pendientes en memoria (mismo patrón que Incidentes/Notas SAP). CIG y
// SUNAT (ConfiguracionCig.tsx/ConfiguracionSunat.tsx) tienen su propio
// guardado, totalmente independiente de este archivo — no se tocan acá.
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Info, Plus } from "lucide-react";
import { obtenerConfigCliente, guardarConfigCliente, ApisCredenciales, ApiDefinition } from "../../services/clienteConexionService.ts";
import { getApiTemplates, ApiTemplate } from "../../services/catalogoApisService.ts";
import { TIPOS_API_LISTA } from "./apiTiposConfig.ts";
import ApiDefinitionCard from "./ApiDefinitionCard.tsx";
import NuevaApiModal from "./NuevaApiModal.tsx";
import ConfirmDialog from "../ui/ConfirmDialog.tsx";
import Alert from "../ui/Alert.tsx";
import { FlujoAprobacionModal } from "../credenciales/FlujoAprobacionCredencial.tsx";

export default function ConfiguracionApis({ clienteId }: { clienteId: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apis, setApis] = useState<ApiDefinition[]>([]);
  // Plantillas del Catálogo de APIs Estándar (Admin) — se pasan tal cual
  // a cada ApiDefinitionCard, que las usa como sugerencia de URL Base/URL
  // del Token por Data Center y para el prellenado rápido por plantilla
  // (ver ApiDefinitionCard.tsx). Un error al cargarlas no bloquea la
  // pantalla: sin catálogo, las tarjetas simplemente no ofrecen sugerencias.
  const [catalogo, setCatalogo] = useState<ApiTemplate[]>([]);
  // Qué tarjetas están expandidas (solo lectura), por id.
  const [expandidas, setExpandidas] = useState<Record<string, boolean>>({});
  // Modal de alta de API nueva (ver NuevaApiModal.tsx) — el tipo (Ariba/
  // S4HANA, del que dependen las credenciales) se elige DENTRO del modal
  // como primer paso; acá solo hace falta saber si está abierto o no.
  const [mostrarNuevaApi, setMostrarNuevaApi] = useState(false);
  // Modal de edición: mismo NuevaApiModal.tsx, pero precargado con esta
  // API (modo edición — ver comentario en NuevaApiModal.tsx). null = cerrado.
  const [apiEditando, setApiEditando] = useState<ApiDefinition | null>(null);
  // API pendiente de confirmar eliminación — ya no hay red de seguridad de
  // "no tocar Guardar configuración", así que borrar pasa por un
  // ConfirmDialog antes de persistir (ver handleConfirmarEliminar).
  const [apiAEliminar, setApiAEliminar] = useState<ApiDefinition | null>(null);
  const [eliminando, setEliminando] = useState(false);
  // API cuyo flujo de aprobación (quién aprueba si alguien pide sus credenciales) está abierto.
  const [apiAprobacion, setApiAprobacion] = useState<ApiDefinition | null>(null);

  // Toast de feedback tras guardar/eliminar (mismo componente/patrón que
  // ValidadorEstructura.tsx) — auto-dismiss, no bloquea la pantalla.
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 4000);
  };

  useEffect(() => {
    getApiTemplates()
      .then(setCatalogo)
      .catch((err) => console.error("Error cargando el catálogo de APIs estándar:", err));
  }, []);

  // Extraída del useEffect inicial para poder recargar después de
  // guardar/eliminar (misma fuente de verdad que el primer GET, en vez de
  // parchear el array en memoria) — mismo criterio que loadIncidentes()/
  // cargar() en IncidentesSection.tsx/NotasSapSection.tsx.
  const cargarApis = useCallback(() => {
    if (!clienteId) { setLoading(false); return Promise.resolve(); }
    setLoading(true);
    return obtenerConfigCliente<ApisCredenciales>(clienteId, "apis")
      .then((config) => setApis(config.apis || []))
      .catch((err) => {
        console.error("Error cargando configuración de APIs:", err);
        setError("No se pudo cargar la configuración guardada.");
      })
      .finally(() => setLoading(false));
  }, [clienteId]);

  useEffect(() => {
    cargarApis();
  }, [cargarApis]);

  // El modal ya persistió en Firestore (ver NuevaApiModal.tsx) — acá solo
  // se recarga la lista desde la fuente real y se cierra.
  const handleGuardadoModal = () => {
    const eraEdicion = !!apiEditando;
    setMostrarNuevaApi(false);
    setApiEditando(null);
    setExpandidas({});
    cargarApis();
    showAlert(eraEdicion ? "API actualizada correctamente." : "API guardada correctamente.");
  };

  const handleCancelarModal = () => {
    setMostrarNuevaApi(false);
    setApiEditando(null);
  };

  const handleConfirmarEliminar = async () => {
    if (!apiAEliminar || eliminando) return;
    setEliminando(true);
    try {
      const restantes = apis.filter((api) => api.id !== apiAEliminar.id);
      await guardarConfigCliente(clienteId, "apis", { apis: restantes });
      setApis(restantes);
      setApiAEliminar(null);
      showAlert("API eliminada correctamente.");
    } catch (err: any) {
      showAlert(err.message || "Error al eliminar la API.", "error");
    } finally {
      setEliminando(false);
    }
  };

  const toggleExpandida = (id: string) => {
    setExpandidas((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>La interfaz para usar estas credenciales en funcionalidades específicas se implementará más adelante — por ahora solo se guardan para tenerlas centralizadas.</span>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">APIs configuradas</h3>
        <button
          type="button"
          onClick={() => setMostrarNuevaApi(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Agregar API
        </button>
      </div>

      {apis.length === 0 ? (
        <p className="text-xs text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-5">
          Todavía no hay APIs configuradas para este cliente. Cada API que consumas (Ariba Supplier Data, S/4HANA Attachment Content, etc.) tiene sus propias credenciales, su propia URL Base + API y sus propios endpoints.
        </p>
      ) : (
        <div className="space-y-8">
          {TIPOS_API_LISTA.map((tipoMeta) => {
            const apisDelTipo = apis.filter((api) => (api.tipo || "ariba") === tipoMeta.id);
            if (apisDelTipo.length === 0) return null;

            return (
              <div key={tipoMeta.id} className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <span className="text-sm leading-none">{tipoMeta.icon}</span> {tipoMeta.label}
                </h4>
                <div className="space-y-5">
                  {apisDelTipo.map((api) => (
                    <ApiDefinitionCard
                      key={api.id}
                      api={api}
                      expandida={!!expandidas[api.id]}
                      catalogo={catalogo}
                      onToggleExpandida={() => toggleExpandida(api.id)}
                      onEditar={() => setApiEditando(api)}
                      onRemove={() => setApiAEliminar(api)}
                      onAprobacion={() => setApiAprobacion(api)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(mostrarNuevaApi || apiEditando) && (
        <NuevaApiModal
          clienteId={clienteId}
          catalogo={catalogo}
          apisActuales={apis}
          apiInicial={apiEditando}
          onGuardado={handleGuardadoModal}
          onCancelar={handleCancelarModal}
        />
      )}

      {apiAprobacion && (
        <FlujoAprobacionModal
          credencial={{ tipo: "api", clienteId, apiId: apiAprobacion.id }}
          titulo={`Aprobación · ${apiAprobacion.nombre || "API"}`}
          onClose={() => setApiAprobacion(null)}
        />
      )}

      {apiAEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta API?"
          message={`Se eliminará "${apiAEliminar.nombre || "esta API"}" y sus credenciales guardadas. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? "Eliminando..." : "Sí, eliminar"}
          cancelLabel="Cancelar"
          danger
          onConfirm={handleConfirmarEliminar}
          onCancel={() => setApiAEliminar(null)}
        />
      )}
    </div>
  );
}
